import { Big } from "big.js";
import { Currency, getCurrencyMinorUnits, MinorUnits } from "./currencies";

// export type MoneyValue<C extends Currency> = BaseMoneyValue<MinorUnits<C>>;
// We have the base type so we can hide the minor units in the exported type
interface BaseMoneyValue<T extends number> {
  readonly amount: Big;
  readonly minorUnits: T;
  readonly name: string;
  readonly operation?: Operation;
}
export type MoneyValue<C extends Currency> = BaseMoneyValue<MinorUnits<C>>;

enum OperationType {
  SUM = "sum",
  SUBTRACT = "subtract",
  MULTIPLY = "multiply",
  DISTRIBUTE = "distribute",
  OFFSET = "offset",
  COMPARE = "compare",
}

export enum ComparisonType {
  MAX = "max",
  MIN = "min",
}

interface BaseOperation {
  readonly type: OperationType;
  readonly reason: string;
}

interface TwoValueOperation extends BaseOperation {
  readonly primaryValue: BaseMoneyValue<number>;
  readonly secondaryValue: BaseMoneyValue<number>;
  readonly type: OperationType.SUBTRACT | OperationType.OFFSET;
}

interface SubtractOperation extends TwoValueOperation {
  readonly type: OperationType.SUBTRACT;
  readonly allowNegativeResult: boolean;
}

interface MultiValueOperation extends BaseOperation {
  readonly values: BaseMoneyValue<number>[];
  readonly type: OperationType.SUM | OperationType.COMPARE;
}

interface CompareOperation extends MultiValueOperation {
  readonly type: OperationType.COMPARE;
  readonly comparisonType: ComparisonType;
}

interface MultiplyOperation extends BaseOperation {
  readonly value: BaseMoneyValue<number>;
  readonly scalar: Big;
  readonly type: OperationType.MULTIPLY;
}

interface DistributionOperation extends BaseOperation {
  readonly value: BaseMoneyValue<number>;
  readonly proportions: Big[];
  readonly proportionIndex: number;
  readonly type: OperationType.DISTRIBUTE;
}

type Operation =
  | SubtractOperation
  | TwoValueOperation
  | MultiValueOperation
  | MultiplyOperation
  | DistributionOperation
  | CompareOperation;

export const moneyValueToString = (moneyValue: MoneyValue<Currency>) => {
  return moneyValue.amount.toFixed(moneyValue.minorUnits);
};

export const moneyValueToNumber = (moneyValue: MoneyValue<Currency>) => {
  return moneyValue.amount.toNumber();
};

export const moneyValueToBig = (moneyValue: MoneyValue<Currency>) => {
  return moneyValue.amount;
};

export namespace MoneyCalculator {
  export class InvalidProportionsError extends Error {
    override name = "InvalidProportionsError";
  }
  export class InvalidMinorUnitsError extends Error {
    override name = "InvalidMinorUnitsError";
  }

  export function newMoneyValue<C extends Currency>({
    amount,
    name,
    currency,
  }: {
    amount: Big;
    name: string;
    currency: C;
  }): BaseMoneyValue<MinorUnits<C>> {
    const minorUnits = getCurrencyMinorUnits(currency);
    return { amount: amount.round(minorUnits), name, minorUnits };
  }

  export function sum<C extends Currency>({
    values,
    reason,
    newValueName,
    currency,
  }: {
    values: MoneyValue<C>[];
    reason: string;
    newValueName: string;
    currency: C;
  }): MoneyValue<C> {
    return {
      amount: values.reduce((acc, value) => acc.plus(value.amount), Big(0)),
      operation: { type: OperationType.SUM, reason, values },
      name: newValueName,
      minorUnits: getCurrencyMinorUnits(currency),
    };
  }

  export function subtract<C extends Currency>({
    primaryValue,
    secondaryValue,
    reason,
    newValueName,
    // If false, the result will be set to 0 if it is negative
    allowNegativeResult = true,
  }: {
    primaryValue: MoneyValue<C>;
    secondaryValue: MoneyValue<C>;
    reason: string;
    newValueName: string;
    allowNegativeResult?: boolean;
  }): MoneyValue<C> {
    let result = primaryValue.amount.minus(secondaryValue.amount);
    if (!allowNegativeResult && result.lt(0)) {
      result = Big(0);
    }
    return {
      amount: result,
      operation: {
        type: OperationType.SUBTRACT,
        reason,
        primaryValue,
        secondaryValue,
        allowNegativeResult,
      },
      name: newValueName,
      minorUnits: primaryValue.minorUnits,
    };
  }

  /** Do NOT use this function for cases where the distribute function could be used instead. */
  export function multiply<T extends number>({
    value,
    scalar,
    reason,
    newValueName,
  }: {
    value: BaseMoneyValue<T>;
    scalar: Big;
    reason: string;
    newValueName: string;
  }): BaseMoneyValue<T> {
    return {
      amount: value.amount.times(scalar).round(value.minorUnits),
      operation: { type: OperationType.MULTIPLY, reason, value, scalar },
      name: newValueName,
      minorUnits: value.minorUnits,
    };
  }

  const _validateProportions = (
    proportions: Big[],
    valueToDistribute: BaseMoneyValue<number>,
  ) => {
    if (valueToDistribute.amount.gt(0) && proportions.length === 0) {
      throw new InvalidProportionsError(
        "Unable to distribute amount: No proportions provided",
      );
    }
  };

  const getNormalizedProportions = (proportions: Big[]) => {
    // If all proportions are 0, set them all to 1
    // so we can distribute the value evenly
    if (proportions.every((proportion) => proportion.eq(0))) {
      return proportions.map(() => Big(1));
    }
    // If all proportions are negative, we need to convert them to positive
    // so the valueToDistribute doesn't flip signs in the _distribute function
    if (proportions.every((proportion) => proportion.lte(0))) {
      return proportions.map((proportion) => proportion.abs());
    }
    // If some proportions are negative and some are positive, set the negative proportions to 0
    // so that negative proportions don't get distributed anything
    return proportions.map((proportion) => {
      if (proportion.lt(0)) {
        return Big(0);
      }
      return proportion;
    });
  };

  export function distribute<T extends number>({
    valueToDistribute,
    proportions: _proportions,
    reason,
    newValueName,
  }: {
    valueToDistribute: BaseMoneyValue<T>;
    proportions: Big[];
    reason: string;
    newValueName: string;
  }): BaseMoneyValue<T>[] {
    _validateProportions(_proportions, valueToDistribute);
    const proportions = getNormalizedProportions(_proportions);

    const results: BaseMoneyValue<T>[] = [];
    let remainingAmountToDistribute = valueToDistribute.amount;

    let sumOfRemainingProportions = proportions.reduce(
      (acc, p) => acc.plus(p),
      new Big(0),
    );

    for (const [i, proportion] of proportions.entries()) {
      let distributedAmount: Big;
      if (sumOfRemainingProportions.eq(0)) {
        distributedAmount = new Big(0);
      } else {
        distributedAmount = remainingAmountToDistribute
          .times(proportion)
          .div(sumOfRemainingProportions)
          .round(valueToDistribute.minorUnits, Big.roundUp);
      }

      results.push({
        amount: distributedAmount,
        operation: {
          type: OperationType.DISTRIBUTE,
          reason,
          value: valueToDistribute,
          proportions,
          proportionIndex: i,
        },
        name: newValueName,
        minorUnits: valueToDistribute.minorUnits,
      });

      remainingAmountToDistribute =
        remainingAmountToDistribute.minus(distributedAmount);
      sumOfRemainingProportions = sumOfRemainingProportions.minus(proportion);
    }

    return results;
  }

  /**
   * Offsets two MoneyValues by subtracting the smaller from the larger.
   * One side is fully zeroed out, and the other holds the remainder.
   *
   * Example:
   *   x = $100, y = $60 → { x: $40, y: $0 }
   *   x = $40, y = $100 → { x: $0, y: $60 }
   */
  export function offsetAmounts<C extends Currency>({
    primaryValue,
    secondaryValue,
    reason,
    newValueNames,
  }: {
    primaryValue: MoneyValue<C>;
    secondaryValue: MoneyValue<C>;
    reason: string;
    newValueNames: { primaryValue: string; secondaryValue: string };
  }): { primaryValue: MoneyValue<C>; secondaryValue: MoneyValue<C> } {
    const operation = {
      type: OperationType.OFFSET,
      reason,
      primaryValue,
      secondaryValue,
    } as const;

    const primaryValueGreater = primaryValue.amount.gt(secondaryValue.amount);

    return {
      primaryValue: {
        amount: primaryValueGreater
          ? primaryValue.amount.minus(secondaryValue.amount)
          : Big(0),
        name: newValueNames.primaryValue,
        minorUnits: primaryValue.minorUnits,
        operation,
      },
      secondaryValue: {
        amount: primaryValueGreater
          ? Big(0)
          : secondaryValue.amount.minus(primaryValue.amount),
        name: newValueNames.secondaryValue,
        minorUnits: secondaryValue.minorUnits,
        operation,
      },
    };
  }

  export function compare<C extends Currency>({
    values,
    type,
    reason,
    newValueName,
  }: {
    // Forces the array to have at least two values
    values: [MoneyValue<C>, MoneyValue<C>, ...MoneyValue<C>[]];
    type: ComparisonType;
    reason: string;
    newValueName: string;
  }): MoneyValue<C> {
    const selected = values.reduce((selected, current) => {
      const preferCurrent =
        type === ComparisonType.MIN
          ? current.amount.lt(selected.amount)
          : current.amount.gt(selected.amount);
      return preferCurrent ? current : selected;
    });
    return {
      amount: selected.amount,
      operation: {
        type: OperationType.COMPARE,
        reason,
        values,
        comparisonType: type,
      },
      name: newValueName,
      minorUnits: selected.minorUnits,
    };
  }
}
